import { JourneyContractSegmentTransportType, type Product, type TransportData } from '@/domainLogic/index.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation.tsx';
import type { TravelExtraPackDataProps } from '@/app/components/search/components/Modal/Modals/TravelExtraPackModal/TravelExtraPackData/types.ts';
import { formatPrice } from '../../../utils/formatting';
import type { Localization } from '../../../common/Localization';

const hasTrainInSegment = (transportData?: TransportData) => {
  const hasInboundTrain = transportData?.inbound?.segments?.find(
    (s) => s?.transport?.type === JourneyContractSegmentTransportType.TRAIN
  );
  const hasOutboundTrain = transportData?.outbound?.segments?.find(
    (s) => s?.transport?.type === JourneyContractSegmentTransportType.TRAIN
  );

  return hasInboundTrain || hasOutboundTrain;
};

export const getTravelExtraPackData = ({
  product,
  localization,
  t,
  transportData,
}: {
  product: Product;
  localization: Localization;
  t: TranslationFunction;
  transportData?: TransportData;
}): TravelExtraPackDataProps => {
  const {
    rate: {
      price: { price },
      tep: { cost: tepCost, discount: tepDiscount, voucher: tepVoucher, enabled: tepEnabled, standardPrice },
    },
  } = product;

  const hasTrain = hasTrainInSegment(transportData);
  const btpTooltipHeading = hasTrain
    ? t('vacation.oss.package_savings.back_to_travel.price_details.tooltip_heading_train')
    : t('vacation.oss.package_savings.back_to_travel.price_details.tooltip_heading');
  const btpTooltipBasicFare = hasTrain
    ? t('vacation.oss.package_savings.back_to_travel.price_details.basic_fare_train')
    : t('vacation.oss.package_savings.back_to_travel.price_details.basic_fare');
  const btpTooltipPack = t('vacation.oss.package_savings.back_to_travel.price_details.pack');
  const btpTooltipDiscount = t('vacation.oss.package_savings.back_to_travel.price_details.discount');
  const btpTooltipNextTrip = t('vacation.oss.package_savings.back_to_travel.price_details.next_trip');
  const btpTooltipFooter = t('vacation.oss.package_savings.back_to_travel.price_details.tooltip_footer');
  const btpTooltipPayNow = t('vacation.oss.package_savings.back_to_travel.price_details.pay_now');

  if (tepEnabled) {
    const list = [];

    if (standardPrice) {
      list.push({
        text: btpTooltipBasicFare,
        amount: formatPrice({
          price: standardPrice,
          localization,
        }),
        hasSeparator: true,
      });
    }

    if (tepCost) {
      list.push({
        text: btpTooltipPack,
        amount: formatPrice({
          price: tepCost,
          localization,
        }),
        hasSeparator: true,
      });
    }

    if (tepDiscount) {
      list.push({
        ...{
          text: btpTooltipDiscount,
          amount: `- ${formatPrice({
            price: tepDiscount,
            localization,
          })}`,
          hasSeparator: true,
        },
      });
    }

    list.push({
      ...{
        text: btpTooltipPayNow,
        amount: formatPrice({
          price,
          localization,
        }),
        isBold: true,
      },
    });

    if (tepVoucher) {
      list.push({
        text: btpTooltipNextTrip
          .replace(
            '__amount__',
            formatPrice({
              price: tepVoucher,
              localization,
            })
          )
          .replace(/\*(.*?)\*/g, '<b>$1</b>'),
        isCard: true,
        iconUrl: { category: 'Financial', name: 'icon-gift-card', color: 'iconPrimary' } as const,
      });
    }

    return {
      backToTravelTooltipHeading: btpTooltipHeading,
      list,
      backToTravelTooltipFooter: btpTooltipFooter,
      pp: t('vacation.oss.cards.pp'),
    };
  }

  return {};
};
